import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from '../../../context/AuthContext';
import {
	acceptRecurringNotification,
	declineRecurringNotification,
	fetchNotifications,
	markGoalNotificationRead,
} from '../api';

const POLL_INTERVAL = 30000; // 30 seconds
const MAX_RETRY_DELAY = 300000; // 5 minutes
const INITIAL_RETRY_DELAY = 1000; // 1 second

// Runs once, inside NotificationsProvider; consumers read it via useNotifications.
const useNotificationsState = () => {
	const { isAuth } = useAuth();
	const { locale } = useIntl();
	const [notifications, setNotifications] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	// Bumped on every request (and on logout) so late responses can be dropped.
	const requestIdRef = useRef(0);

	// One fetch. Never throws; resolves to whether it succeeded.
	const fetchPending = useCallback(async () => {
		if (!isAuth) return true;
		const requestId = ++requestIdRef.current;
		try {
			const response = await fetchNotifications('pending', 50, 0, locale);
			if (requestId !== requestIdRef.current) return true; // superseded
			setNotifications(response?.notifications ?? []);
			setError(null);
			return true;
		} catch (err) {
			if (requestId !== requestIdRef.current) return true; // superseded
			console.error('Error fetching notifications:', err);
			setError(err.message || 'Failed to fetch notifications');
			return false;
		} finally {
			if (requestId === requestIdRef.current) setIsLoading(false);
		}
	}, [isAuth, locale]);

	// Optimistically drop the item, call the API, then re-sync with the server
	// (also on failure, which reverts the optimistic removal).
	const removeThenSync = useCallback(
		async (apiCall, notificationId, action) => {
			setNotifications(prev => prev.filter(n => n.id !== notificationId));
			try {
				await apiCall(notificationId, locale);
			} catch (err) {
				console.error(`Error ${action} notification:`, err);
				throw err;
			} finally {
				await fetchPending();
			}
		},
		[fetchPending, locale],
	);

	const acceptNotification = useCallback(
		id => removeThenSync(acceptRecurringNotification, id, 'accepting'),
		[removeThenSync],
	);
	const declineNotification = useCallback(
		id => removeThenSync(declineRecurringNotification, id, 'declining'),
		[removeThenSync],
	);
	const markAsRead = useCallback(
		id => removeThenSync(markGoalNotificationRead, id, 'marking as read'),
		[removeThenSync],
	);

	// Poll while authenticated and the tab is visible; back off after failures.
	useEffect(() => {
		if (!isAuth) {
			requestIdRef.current += 1; // drop any in-flight response
			setNotifications([]);
			setError(null);
			return;
		}

		let timer = null;
		let stopped = false;
		let retryDelay = INITIAL_RETRY_DELAY;

		const schedule = delay => {
			clearTimeout(timer);
			timer = setTimeout(poll, delay);
		};

		const poll = async () => {
			clearTimeout(timer);
			if (document.hidden) return;
			const succeeded = await fetchPending();
			if (stopped || document.hidden) return;
			if (succeeded) {
				retryDelay = INITIAL_RETRY_DELAY;
				schedule(POLL_INTERVAL);
			} else {
				schedule(retryDelay);
				retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY);
			}
		};

		const handleVisibilityChange = () => {
			if (document.hidden) clearTimeout(timer);
			else poll();
		};

		setIsLoading(true);
		poll();
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			stopped = true;
			clearTimeout(timer);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [isAuth, fetchPending]);

	return {
		notifications,
		pendingCount: notifications.length,
		isLoading,
		error,
		acceptNotification,
		declineNotification,
		markAsRead,
		refreshNotifications: fetchPending,
	};
};

const NotificationsContext = createContext(null);

export const NotificationsProvider = ({ children }) => {
	const state = useNotificationsState();

	const {
		notifications,
		isLoading,
		error,
		acceptNotification,
		declineNotification,
		markAsRead,
		refreshNotifications,
	} = state;

	const value = useMemo(
		() => ({
			notifications,
			pendingCount: notifications.length,
			isLoading,
			error,
			acceptNotification,
			declineNotification,
			markAsRead,
			refreshNotifications,
		}),
		[
			notifications,
			isLoading,
			error,
			acceptNotification,
			declineNotification,
			markAsRead,
			refreshNotifications,
		],
	);

	return (
		<NotificationsContext.Provider value={value}>
			{children}
		</NotificationsContext.Provider>
	);
};

export const useNotifications = () => {
	const ctx = useContext(NotificationsContext);
	if (!ctx) {
		throw new Error('useNotifications must be used within NotificationsProvider');
	}
	return ctx;
};
